import { call, put, takeLatest } from "redux-saga/effects";
import { ApiError } from "@/lib/apiClient";
import { PaginatedApiResponse } from "@/lib/apiClient";
import {
  getWholesalersCollections,
  importWholesalersCollections,
  removeWholesalersCollections,
} from "../actions/wholesaler.actions";
import { Category } from "@/types/models/category";
import {
  addWholesalerCollectionFailure,
  addWholesalerCollectionRequest,
  addWholesalerCollectionSuccess,
  detachWholesalerCollectionFailure,
  detachWholesalerCollectionRequest,
  detachWholesalerCollectionSuccess,
  fetchWholesalerCollectionFailure,
  fetchWholesalerCollectionRequest,
  fetchWholesalerCollectionSuccess,
} from "../slices/wholesalerCollection.slice";
import { toast } from "sonner";

function* fetchWholesalerCollectionsSaga(
  action: ReturnType<typeof fetchWholesalerCollectionRequest>,
): Generator<unknown, void, unknown> {
  try {
    const data = (yield call(getWholesalersCollections, {
      id: action.payload.id,
      page: action.payload.page,
      pageSize: action.payload.pageSize,
    })) as PaginatedApiResponse<Category[]>;
    yield put(
      fetchWholesalerCollectionSuccess({
        id: action.payload.id,
        data: data.data,
        pagination: {
          page: data.pagination.page,
          total: data.pagination.total,
          pageSize: data.pagination.pageSize,
          totalPages: data.pagination.totalPages,
        },
      }),
    );
  } catch (err: unknown) {
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || "Something went wrong!",
          );
    toast.error(normalized.message || "Something went wrong!");
    yield put(
      fetchWholesalerCollectionFailure({
        id: action.payload.id,
        error: normalized,
      }),
    );
  }
}

function* addWholesalerCollectionsSaga(
  action: ReturnType<typeof addWholesalerCollectionRequest>,
) {
  try {
    const response = (yield call(importWholesalersCollections, {
      id: action.payload.id,
      margin: action.payload.margin,
      margin_type: action.payload.margin_type,
      payment_type: action.payload.payment_type,
      collection_ids: action.payload.collection_ids,
    })) as { message?: string };
    toast.success(response?.message || "Collections imported successfully");
    yield put(
      addWholesalerCollectionSuccess({
        id: action.payload.id,
        data: action.payload.collection_ids,
      }),
    );
  } catch (err: unknown) {
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || "Something went wrong!",
          );
    toast.error(normalized.message || "Something went wrong!");
    yield put(
      addWholesalerCollectionFailure({
        id: action.payload.id,
        error: normalized,
      }),
    );
  }
}

function* removeWholesalerCollectionsSaga(
  action: ReturnType<typeof detachWholesalerCollectionRequest>,
) {
  try {
    yield call(removeWholesalersCollections, {
      id: action.payload.id,
      collection_ids: action.payload.collection_ids,
    });
    toast.success("Collection removed successfully");
    yield put(
      detachWholesalerCollectionSuccess({
        id: action.payload.id,
        data: action.payload.collection_ids,
      }),
    );
  } catch (err: unknown) {
    const normalized =
      err instanceof ApiError
        ? err
        : new ApiError(
            500,
            "UNKNOWN_ERROR",
            (err as any)?.message || "Something went wrong!",
          );
    toast.error(normalized.message || "Something went wrong!");
    yield put(
      detachWholesalerCollectionFailure({
        id: action.payload.id,
        error: normalized,
      }),
    );
  }
}

export default function* wholesalerCollectionsRootSaga() {
  yield takeLatest(
    fetchWholesalerCollectionRequest.type,
    fetchWholesalerCollectionsSaga,
  );
  yield takeLatest(
    addWholesalerCollectionRequest.type,
    addWholesalerCollectionsSaga,
  );
  yield takeLatest(
    detachWholesalerCollectionRequest.type,
    removeWholesalerCollectionsSaga,
  );
}
